import { createRoute, Link, useNavigate } from '@tanstack/react-router';
import { Route as rootRoute } from '../__root';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { doc, getDoc, updateDoc, increment, collection, query, where, getDocs, addDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useCartStore } from '../../store';
import { ArrowLeft, ShoppingCart, Tag, Store, Package, Ruler, Layers, MessageCircle, Heart, Star, ChevronRight, Home } from 'lucide-react';
import { toast } from 'sonner';
import { ProductQA } from '../../components/ProductQA';
import { ProductReviews } from '../../components/ProductReviews';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/product/$productId',
  component: ProductPage,
});

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  imageUrl?: string;
  category?: string;
  tags?: string[];
  vendorId?: string;
  vendorName?: string;
  isArchived?: boolean;
  stock?: number;
  maxOrderQuantity?: number;
  dimensions?: string;
  details?: string;
  views?: number;
  favorites?: number;
  ratingAverage?: number;
  ratingCount?: number;
}

function ProductPage() {
  const { productId } = Route.useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [isFavorited, setIsFavorited] = useState(false);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [favoriteDocId, setFavoriteDocId] = useState<string | null>(null);
  const addItem = useCartStore((state) => state.addItem);

  // View tracking and product fetching
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const productRef = doc(db, 'products', productId);
        const productDoc = await getDoc(productRef);
        if (productDoc.exists()) {
          const data = productDoc.data() as Product;
          setProduct({ ...data, id: productDoc.id });
          setFavoritesCount(data.favorites || 0);

          // Track view - increment asynchronously 
          // Do not await this to avoid blocking the UI, catch errors silently
          updateDoc(productRef, {
             views: increment(1)
          }).catch(() => {});
        }
      } catch (err) {
        console.error('Error fetching product:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  // Check if user has favorited
  useEffect(() => {
    const checkFavorite = async () => {
      if (!auth.currentUser || !product) return;
      try {
        const q = query(
          collection(db, 'favorite_products'),
          where('userId', '==', auth.currentUser.uid),
          where('productId', '==', product.id)
        );
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          setIsFavorited(true);
          setFavoriteDocId(snapshot.docs[0].id);
        }
      } catch (err) {
        console.error("Error checking favorite:", err);
      }
    };
    checkFavorite();
  }, [product, auth.currentUser]);

  const handleToggleFavorite = async () => {
    if (!auth.currentUser) {
      toast.error('Please sign in to save products');
      return;
    }
    if (!product) return;

    try {
      if (isFavorited && favoriteDocId) {
        // Remove favorite
        await deleteDoc(doc(db, 'favorite_products', favoriteDocId));
        await updateDoc(doc(db, 'products', product.id), {
          favorites: increment(-1)
        });
        setIsFavorited(false);
        setFavoritesCount(prev => Math.max(0, prev - 1));
        setFavoriteDocId(null);
      } else {
        // Add favorite
        const favRef = await addDoc(collection(db, 'favorite_products'), {
          userId: auth.currentUser.uid,
          productId: product.id,
          createdAt: serverTimestamp()
        });
        await updateDoc(doc(db, 'products', product.id), {
          favorites: increment(1)
        });
        setIsFavorited(true);
        setFavoritesCount(prev => prev + 1);
        setFavoriteDocId(favRef.id);
      }
    } catch (err) {
      console.error("Error toggling favorite:", err);
      toast.error('Failed to update favorite status');
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex justify-center items-center h-[60vh] bg-neutral-50 dark:bg-neutral-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900 dark:border-white"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex-1 flex flex-col justify-center items-center h-[60vh] text-center bg-neutral-50 dark:bg-neutral-900">
        <Package className="w-16 h-16 text-neutral-300 dark:text-neutral-600 mb-4" />
        <h2 className="text-2xl font-serif text-neutral-900 dark:text-white">Product Not Found</h2>
        <p className="text-neutral-500 dark:text-neutral-400 mt-2">This item may have been removed or is currently unavailable.</p>
        <button onClick={() => navigate({ to: '/' })} className="mt-6 text-sm font-medium text-neutral-900 dark:text-white hover:underline">Return to Market</button>
      </div>
    );
  }

  const handleAddToCart = () => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      quantity,
      imageUrl: product.imageUrl,
      description: product.description,
      vendorId: product.vendorId,
      vendorName: product.vendorName,
      stock: product.stock,
      maxOrderQuantity: product.maxOrderQuantity
    });
    toast.success('Added to cart');
  };

  const getStockStatus = () => {
    if (product.stock === undefined) return { label: 'In Stock (Pre-order)', color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200 dark:border-green-900/50' };
    if (product.stock > 0) return { label: `${product.stock} in stock`, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-900/50' };
    return { label: 'Out of Stock', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-900/50' };
  };

  const stockStatus = getStockStatus();
  const maxAllowed = product.stock !== undefined ? product.stock : (product.maxOrderQuantity !== undefined ? product.maxOrderQuantity : 5);
  const isOutOfStock = product.stock !== undefined && product.stock <= 0;

  return (
    <div className="bg-neutral-50 dark:bg-neutral-900 min-h-screen py-8">
      <Helmet>
        <title>{product.name} | Cardston Market</title>
        <meta name="description" content={product.description} />
        <link rel="canonical" href={`https://www.cardstonmarket.com/product/${product.id}`} />
        <meta property="og:title" content={`${product.name} | Cardston Market`} />
        <meta property="og:description" content={product.description} />
        {product.imageUrl && <meta property="og:image" content={product.imageUrl} />}
        <meta property="og:type" content="product" />
        <meta property="og:url" content={`https://www.cardstonmarket.com/product/${product.id}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${product.name} | Cardston Market`} />
        <meta name="twitter:description" content={product.description} />
        {product.imageUrl && <meta name="twitter:image" content={product.imageUrl} />}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": product.name,
          "description": product.description,
          "image": product.imageUrl,
          "url": `https://www.cardstonmarket.com/product/${product.id}`,
          "offers": {
            "@type": "Offer",
            "price": (product.price / 100).toFixed(2),
            "priceCurrency": "CAD",
            "availability": product.stock !== undefined
              ? (product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock")
              : "https://schema.org/InStock",
            "seller": product.vendorName ? { "@type": "Organization", "name": product.vendorName } : undefined,
            "priceValidUntil": new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
          },
          ...(product.ratingCount ? {
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": product.ratingAverage?.toFixed(1),
              "reviewCount": product.ratingCount
            }
          } : {}),
          ...(product.vendorId ? {
            "brand": { "@type": "Brand", "name": product.vendorName || "Local Vendor" }
          } : {}),
          ...(product.category ? { "category": product.category } : {}),
          ...(product.tags ? { "keywords": product.tags.join(', ') } : {})
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.cardstonmarket.com/" },
            ...(product.vendorId ? [
              { "@type": "ListItem", "position": 2, "name": product.vendorName || "Vendor", "item": `https://www.cardstonmarket.com/shop/${product.vendorId}` }
            ] : []),
            { "@type": "ListItem", "position": product.vendorId ? 3 : 2, "name": product.name, "item": `https://www.cardstonmarket.com/product/${product.id}` }
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": product.name,
          "description": product.description,
          "url": `https://www.cardstonmarket.com/product/${product.id}`,
          "isPartOf": { "@id": "https://www.cardstonmarket.com/#website" },
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": ["h1", ".product-description"]
          },
          "primaryImageOfPage": product.imageUrl ? { "@type": "ImageObject", "url": product.imageUrl } : undefined
        })}</script>
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm text-neutral-500 dark:text-neutral-400 mb-8 flex-wrap">
          <Link to="/" className="hover:text-neutral-900 dark:hover:text-white transition-colors flex items-center gap-1">
            <Home className="w-3.5 h-3.5" /> Home
          </Link>
          {product.vendorId && (
            <>
              <ChevronRight className="w-3.5 h-3.5 shrink-0" />
              <Link to="/shop/$vendorId" params={{ vendorId: product.vendorId }} className="hover:text-neutral-900 dark:hover:text-white transition-colors">
                {product.vendorName || 'Vendor'}
              </Link>
            </>
          )}
          <ChevronRight className="w-3.5 h-3.5 shrink-0" />
          <span className="text-neutral-900 dark:text-white font-medium truncate max-w-[200px] sm:max-w-none">{product.name}</span>
        </nav>

        <div className="bg-white dark:bg-neutral-950 rounded-2xl shadow-sm border border-neutral-200 dark:border-neutral-800 overflow-hidden flex flex-col md:flex-row">
          
          {/* Product Image Section */}
          <div className="md:w-1/2 bg-neutral-100 dark:bg-neutral-800 min-h-[400px] relative">
            <img 
              src={product.imageUrl || 'https://picsum.photos/seed/product/800/800'} 
              alt={`Handmade ${product.name} by ${product.vendorName || 'Local Artisan'} on Cardston Market`}
              className="w-full h-full object-cover absolute inset-0"
              fetchPriority="high"
            />
          </div>

          {/* Product Details Section */}
          <div className="md:w-1/2 p-8 lg:p-12 flex flex-col">
             <div className="flex justify-end mb-2">
               <button 
                 onClick={handleToggleFavorite}
                 className="flex items-center gap-1.5 text-sm font-medium transition-colors"
                 style={{ color: isFavorited ? '#ef4444' : 'currentColor' }}
               >
                 <Heart className={`w-5 h-5 ${isFavorited ? 'fill-red-500 text-red-500' : 'text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'}`} />
                 <span className={isFavorited ? 'text-red-500' : 'text-neutral-500 dark:text-neutral-400'}>{favoritesCount > 0 ? favoritesCount : ''}</span>
               </button>
             </div>
            <div className="flex-1">
              <div className="flex justify-between items-start gap-4 mb-4">
                <h1 className="text-3xl sm:text-4xl font-serif font-medium text-neutral-900 dark:text-white tracking-tight">{product.name}</h1>
                <p className="text-2xl font-serif text-neutral-900 dark:text-white shrink-0">${(product.price / 100).toFixed(2)}</p>
              </div>

              {product.vendorId && (
                <Link to={'/shop/$vendorId'} params={{ vendorId: product.vendorId }} className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline mb-6">
                  <Store className="w-4 h-4" />
                  {product.vendorName || 'Local Vendor'}
                </Link>
              )}
              
              {product.ratingCount ? (
                <div className="flex items-center gap-2 mb-6 text-sm text-neutral-600 dark:text-neutral-400">
                  <div className="flex text-yellow-400">
                     <Star className="w-4 h-4 fill-current" />
                  </div>
                  <span className="font-medium text-neutral-900 dark:text-white">{product.ratingAverage?.toFixed(1)}</span>
                  <span>({product.ratingCount} {product.ratingCount === 1 ? 'review' : 'reviews'})</span>
                </div>
              ) : null}

              <div className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border mb-6 ${stockStatus.bg} ${stockStatus.color} ${stockStatus.border}`}>
                {stockStatus.label}
              </div>

              <div className="prose prose-neutral dark:prose-invert max-w-none mb-8 text-neutral-600 dark:text-neutral-300 product-description">
                <p>{product.description}</p>
              </div>

              {/* Product Specifications */}
              {(product.dimensions || product.details || product.category) && (
                <div className="bg-neutral-50 dark:bg-neutral-900 rounded-xl p-6 mb-8 border border-neutral-100 dark:border-neutral-800 space-y-4">
                  <h3 className="text-sm font-medium text-neutral-900 dark:text-white mb-2 uppercase tracking-wider">Product Details</h3>
                  {product.category && (
                    <div className="flex items-start gap-3">
                      <Tag className="w-4 h-4 text-neutral-400 mt-0.5 shrink-0" />
                      <div>
                        <span className="block text-xs text-neutral-500 uppercase tracking-wider">Category</span>
                        <span className="text-sm text-neutral-900 dark:text-white">{product.category}</span>
                      </div>
                    </div>
                  )}
                  {product.dimensions && (
                    <div className="flex items-start gap-3">
                      <Ruler className="w-4 h-4 text-neutral-400 mt-0.5 shrink-0" />
                      <div>
                        <span className="block text-xs text-neutral-500 uppercase tracking-wider">Dimensions</span>
                        <span className="text-sm text-neutral-900 dark:text-white">{product.dimensions}</span>
                      </div>
                    </div>
                  )}
                  {product.details && (
                    <div className="flex items-start gap-3">
                      <Layers className="w-4 h-4 text-neutral-400 mt-0.5 shrink-0" />
                      <div>
                        <span className="block text-xs text-neutral-500 uppercase tracking-wider">Details</span>
                        <span className="text-sm text-neutral-900 dark:text-white">{product.details}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {product.tags && product.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-8">
                  {product.tags.map(tag => (
                    <span key={tag} className="px-3 py-1 bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 text-xs font-medium rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="pt-8 border-t border-neutral-100 dark:border-neutral-800 mt-auto">
              {isOutOfStock ? (
                <button disabled className="w-full py-4 rounded-full bg-neutral-200 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 font-medium cursor-not-allowed">
                  Out of Stock
                </button>
              ) : (
                <div className="flex items-center gap-4">
                  <div className="flex items-center bg-neutral-100 dark:bg-neutral-800 rounded-full p-1 border border-neutral-200 dark:border-neutral-700">
                    <button 
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                      className="w-10 h-10 flex items-center justify-center rounded-full text-neutral-600 dark:text-neutral-400 hover:bg-white dark:hover:bg-neutral-700 disabled:opacity-50 transition-colors"
                    >
                      -
                    </button>
                    <span className="w-10 text-center font-medium text-neutral-900 dark:text-white">{quantity}</span>
                    <button 
                      onClick={() => setQuantity(Math.min(maxAllowed, quantity + 1))}
                      disabled={quantity >= maxAllowed}
                      className="w-10 h-10 flex items-center justify-center rounded-full text-neutral-600 dark:text-neutral-400 hover:bg-white dark:hover:bg-neutral-700 disabled:opacity-50 transition-colors"
                    >
                      +
                    </button>
                  </div>
                  <button 
                    onClick={handleAddToCart}
                    className="flex-1 flex justify-center items-center gap-2 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 py-4 rounded-full font-medium hover:bg-neutral-800 dark:hover:bg-neutral-200 transition-colors shadow-sm"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    Add to Cart
                  </button>
                </div>
              )}
              {!isOutOfStock && product.stock === undefined && (
                <p className="text-center text-xs text-neutral-500 mt-3 flex items-center justify-center gap-1">
                  <Tag className="w-3.5 h-3.5" /> Made to order. Limit {maxAllowed} per customer.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Public Q&A Section */}
        <ProductQA productId={product.id} vendorId={product.vendorId} />

        {/* Customer Reviews Section */}
        <ProductReviews productId={product.id} vendorId={product.vendorId} />
      </div>
    </div>
  );
}
